import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { crypto as cv, type dto } from '@photo-vault/shared';
import { Card, Icon, PageHeader } from '../../ui/index.js';
import { useAuthSession } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { rememberAlbumKey } from '../albums/albumKeys.js';

interface ShareRow {
  share: dto.IncomingShare;
  rootName: string;
}

// Lists albums shared *with* the current user (viewer perspective). Each entry
// is decrypted on-device: K_album is unwrapped from the share's wrappedKey and
// cached so the SharedAlbumPage can reuse it without another unwrap.
export function SharedListPage() {
  const session = useAuthSession();
  const { metadata } = useInfra();
  const [rows, setRows] = useState<ShareRow[] | 'loading' | { error: string }>('loading');

  useEffect(() => {
    let cancelled = false;
    setRows('loading');
    (async () => {
      try {
        const { shares } = await metadata.listIncomingShares(session.jwt);
        if (shares.length === 0) {
          if (!cancelled) setRows([]);
          return;
        }
        const out: ShareRow[] = [];
        for (const share of shares) {
          try {
            const albumKey = await cv.unwrapAlbumKey(share.wrappedAlbumKey, session.rsaPriv);
            rememberAlbumKey(share.rootAlbumId, albumKey);
            // Resolve the root album encryptedName via the tree call. The
            // tree includes the root itself as a node.
            const { nodes } = await metadata.fetchAlbumTree(share.rootAlbumId, session.jwt);
            const rootNode = nodes.find((n) => n.id === share.rootAlbumId);
            const rootName = rootNode
              ? await cv
                  .decryptText(rootNode.encryptedName, albumKey)
                  .catch(() => '(decrittura fallita)')
              : share.rootAlbumId;
            out.push({ share, rootName });
          } catch (err) {
            out.push({
              share,
              rootName: `(errore: ${err instanceof Error ? err.message : 'unwrap fallito'})`,
            });
          }
        }
        if (!cancelled) setRows(out);
      } catch (err) {
        if (!cancelled) setRows({ error: err instanceof Error ? err.message : 'errore' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session.jwt, session.rsaPriv, metadata]);

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow="condivisi con me"
        title={
          <>
            Album <span className="accent-text">condivisi</span>
          </>
        }
        description="La chiave di ogni album viene cifrata sul device del proprietario per la tua chiave pubblica. Solo tu puoi sbloccarla."
      />

      {rows === 'loading' ? (
        <p className="text-sm text-on-surface-variant">Carico…</p>
      ) : 'error' in rows && !Array.isArray(rows) ? (
        <p className="text-sm text-error">Errore: {(rows as { error: string }).error}</p>
      ) : (rows as ShareRow[]).length === 0 ? (
        <Card className="flex flex-col items-center gap-2 py-12 text-center">
          <Icon name="folder_shared" className="text-4xl text-on-surface-variant" />
          <p className="text-sm text-on-surface-variant">
            Nessun album condiviso con te al momento.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {(rows as ShareRow[]).map((row) => (
            <Link
              key={row.share.rootAlbumId}
              to={`/shared/${encodeURIComponent(row.share.rootAlbumId)}`}
              className="block"
            >
              <Card className="group flex h-full flex-col gap-4 transition-all hover:border-on-surface-variant/40 hover:shadow-elevated">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan/10 text-cyan">
                    <Icon name="folder_shared" className="text-xl" />
                  </span>
                  <h3 className="flex-1 truncate font-display text-base font-semibold">
                    {row.rootName}
                  </h3>
                  <Icon
                    name="arrow_forward"
                    className="text-base text-on-surface-variant transition-transform group-hover:translate-x-0.5"
                  />
                </div>
                <p className="text-xs text-on-surface-variant">da {row.share.ownerEmail}</p>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
